import { useMemo, useState } from 'react';
import { Clock, ArrowRight, Sparkles } from 'lucide-react';

import { useCommandPalette } from './useCommandPalette';
import { generateNavigationCommands, generateActionCommands } from './commands';

import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
} from '@/components/ui/command';

/**
 * Command Palette Dialog
 * Dark-themed search interface for quick navigation and actions
 */
const CommandPalette = ({ canAccessSection }) => {
  const { isOpen, closePalette, recentCommands, executeCommand } = useCommandPalette();
  const [search, setSearch] = useState('');

  // Generate commands based on user permissions
  const navigationCommands = useMemo(
    () => generateNavigationCommands(canAccessSection),
    [canAccessSection]
  );

  const actionCommands = useMemo(
    () => generateActionCommands(canAccessSection),
    [canAccessSection]
  );

  // Create a lookup map of all commands by ID for resolving recent commands
  // This ensures we get the icon from the actual command definition, not from localStorage
  const commandsById = useMemo(() => {
    const map = {};
    navigationCommands.forEach((cmd) => {
      map[cmd.id] = cmd;
    });
    actionCommands.forEach((cmd) => {
      map[cmd.id] = cmd;
    });
    return map;
  }, [navigationCommands, actionCommands]);

  // Resolve recent commands from stored IDs to full command objects with icons
  const resolvedRecentCommands = useMemo(() => {
    return recentCommands
      .map((stored) => {
        // Look up the full command definition to get the icon
        const fullCommand = commandsById[stored.id];
        if (fullCommand) {
          return fullCommand;
        }
        // If command no longer exists (e.g., user lost permission), skip it
        return null;
      })
      .filter(Boolean);
  }, [recentCommands, commandsById]);

  // Group navigation commands by category
  const groupedNavigation = useMemo(() => {
    const groups = {};
    navigationCommands.forEach((cmd) => {
      const category = cmd.category || 'Other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(cmd);
    });
    return groups;
  }, [navigationCommands]);

  const handleSelect = (command) => {
    executeCommand(command);
    setSearch('');
  };

  const hasSearch = search.length > 0;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closePalette();
          setSearch('');
        }
      }}
      className="admin-command-palette"
    >
      <CommandInput
        placeholder="Search pages, actions..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Recent Commands - only show when not searching */}
        {!hasSearch && resolvedRecentCommands.length > 0 && (
          <CommandGroup heading="Recent">
            {resolvedRecentCommands.map((cmd) => {
              const IconComponent = cmd.icon;
              return (
                <CommandItem
                  key={`recent-${cmd.id}`}
                  value={`recent ${cmd.id}`}
                  onSelect={() => handleSelect(cmd)}
                >
                  <Clock className="h-4 w-4 text-amber-500/70" />
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  <span>{cmd.label}</span>
                  <CommandShortcut>
                    <ArrowRight className="h-3 w-3" />
                  </CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Actions */}
        {actionCommands.length > 0 && (
          <CommandGroup heading="Actions">
            {actionCommands.map((cmd) => {
              const IconComponent = cmd.icon;
              return (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${cmd.keywords.join(' ')}`}
                  onSelect={() => handleSelect(cmd)}
                >
                  <Sparkles className="h-4 w-4 text-amber-500" />
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  <div className="flex flex-col">
                    <span>{cmd.label}</span>
                    <span className="text-xs text-muted-foreground">{cmd.description}</span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Navigation - grouped by category */}
        {Object.entries(groupedNavigation).map(([category, commands]) => (
          <CommandGroup key={category} heading={category}>
            {commands.map((cmd) => {
              const IconComponent = cmd.icon;
              return (
                <CommandItem
                  key={cmd.id}
                  value={`${cmd.label} ${cmd.keywords.join(' ')}`}
                  onSelect={() => handleSelect(cmd)}
                >
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  <div className="flex flex-col">
                    <span>{cmd.label}</span>
                    <span className="text-xs text-muted-foreground">{cmd.description}</span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
